import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useIntl } from 'react-intl';

import PillButton from '../buttons/Pill';
import ArrowIcon from '../icons/Arrow';
import CloseIcon from '../icons/Close';
import RecentSearches from './RecentSearches';

import styles from '../../../styles/partials/top-search.module.scss';

const propTypes = {
    value: PropTypes.string,
    onSubmit: PropTypes.func,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    isFocusable: PropTypes.bool,
    className: PropTypes.string,
    searchWrapClassName: PropTypes.string,
    searchInnerClassName: PropTypes.string,
    searchFormClassName: PropTypes.string,
    searchBarClassName: PropTypes.string,
    searchInputClassName: PropTypes.string,
    searchSubmitBtnClassName: PropTypes.string,
};

const defaultProps = {
    value: null,
    onSubmit: null,
    onChange: null,
    onBlur: null,
    isFocusable: true,
    className: null,
    searchWrapClassName: null,
    searchInnerClassName: null,
    searchFormClassName: null,
    searchBarClassName: null,
    searchInputClassName: null,
    searchSubmitBtnClassName: null,
};

const TopSearch = ({
    value,
    onSubmit,
    onChange,
    onBlur: onParentBlur,
    isFocusable,
    className,
    searchWrapClassName,
    searchInnerClassName,
    searchFormClassName,
    searchBarClassName,
    searchInputClassName,
    searchSubmitBtnClassName,
}) => {
    const searchInputRef = useRef(null);
    const intl = useIntl();
    const [showClearSearch, setShowClearSearch] = useState(false);

    const onInputChange = useCallback(
        (e) => {
            if (onChange !== null) {
                onChange(e.target.value);
            }
        },
        [onChange],
    );

    const onClickRecentSearch = useCallback(
        (recentSearch) => {
            if (onSubmit !== null) {
                onSubmit(recentSearch);
            }
        },
        [onSubmit, onChange],
    );

    const onBlur = useCallback(
        (e) => {
            if (onParentBlur !== null) {
                onParentBlur(e);
            }
        },
        [onParentBlur],
    );

    const onClickClearSearch = useCallback(() => {
        if (onChange !== null) {
            onChange('');
        }
        if (onSubmit !== null) {
            onSubmit('');
        }
    }, [onChange, onSubmit]);

    useEffect(() => {
        setShowClearSearch(value);
    }, [value, setShowClearSearch]);

    // TODO: make this idea work in ios (works in android)
    // hypothesis — call a .click() on the element first?
    // Otherwise, .setAttribute('autofocus', 'autofocus'). .removeAttribute('autofocus') when not active

    useEffect(() => {
        if (isFocusable) searchInputRef.current.focus(); // focus search input when search bar becomes visible
    }, [isFocusable]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <div
                className={classNames([
                    styles.searchWrap,
                    {
                        [searchWrapClassName]: searchWrapClassName !== null,
                    },
                ])}
            >
                <div
                    className={classNames([
                        styles.searchInner,
                        {
                            [searchInnerClassName]: searchInnerClassName !== null,
                        },
                    ])}
                >
                    <form
                        className={classNames([
                            styles.searchForm,
                            {
                                [searchFormClassName]: searchFormClassName !== null,
                            },
                        ])}
                        onSubmit={(e) => {
                            e.preventDefault();
                            onSubmit(value);
                        }}
                    >
                        <div
                            className={classNames([
                                styles.searchBar,
                                {
                                    [searchBarClassName]: searchBarClassName !== null,
                                },
                            ])}
                        >
                            <input
                                ref={searchInputRef}
                                className={classNames([
                                    styles.searchInput,
                                    {
                                        [searchInputClassName]: searchInputClassName !== null,
                                    },
                                ])}
                                type="text"
                                placeholder={intl.formatMessage({
                                    defaultMessage: 'Search',
                                    description: 'Input placeholder',
                                })}
                                tabIndex={isFocusable ? '' : '-1'}
                                value={value || ''}
                                onChange={onInputChange}
                                onBlur={onBlur}
                            />
                            {showClearSearch ? (
                                <PillButton
                                    className={styles.searchClearBtn}
                                    onClick={onClickClearSearch}
                                    icon={<CloseIcon />}
                                    type="button"
                                    tabIndex={isFocusable ? '' : '-1'}
                                    title={intl.formatMessage({
                                        defaultMessage: 'Clear search',
                                        description: 'Button label',
                                    })}
                                    isDark
                                />
                            ) : null}
                            <PillButton
                                className={classNames([
                                    styles.searchSubmitBtn,
                                    {
                                        [searchSubmitBtnClassName]:
                                            searchSubmitBtnClassName !== null,
                                    },
                                ])}
                                icon={<ArrowIcon />}
                                type="submit"
                                tabIndex={isFocusable ? '' : '-1'}
                                title={intl.formatMessage({
                                    defaultMessage: 'Search',
                                    description: 'Button label',
                                })}
                            />
                        </div>
                    </form>

                    <RecentSearches
                        className={styles.pastSearches}
                        onClickItem={onClickRecentSearch}
                    />
                </div>
            </div>
        </div>
    );
};

TopSearch.propTypes = propTypes;
TopSearch.defaultProps = defaultProps;

export default TopSearch;
